import { inject, Injectable, InjectionToken } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { getRoleIdFromAccessToken, isStoredAccessTokenExpired } from '../../auth-session.util';

/**
 * Supported site layouts.
 * `0` = classic/default design (template 0), `1` = OTT layout (template 1).
 */
export type SiteTemplate = 0 | 1;

/**
 * Minimal shape this service needs from a CMS section row.
 *
 * `template_id` does not exist in the API today — it is declared optional so the
 * per-row seam below already compiles against tomorrow's response without
 * forcing a speculative field onto the real `CmsLayoutRow` model.
 */
export interface TemplateAwareRow {
  readonly template_id?: number | string | null;
}

/** Every value that is not an explicit `1` falls back to the classic design. */
const DEFAULT_TEMPLATE: SiteTemplate = 0;

/**
 * Structural view of the environment config for template lookup only.
 *
 * `template` is declared **only in `environment.ts`** — the 24 tenant files swapped
 * in by angular.json `fileReplacements` do not carry it, so reading
 * `environment.template` directly would fail to compile for those builds. Typing
 * the property as optional here keeps every tenant compiling and lets them fall
 * through to {@link DEFAULT_TEMPLATE}.
 */
interface TemplateConfig {
  readonly template?: unknown;
}

/**
 * Raw template id from the active environment file.
 * Tests override this so specs stay classic even when `environment.ts` sets `template: 1`.
 */
export const ENVIRONMENT_TEMPLATE = new InjectionToken<unknown>('ENVIRONMENT_TEMPLATE', {
  providedIn: 'root',
  factory: () => (environment as TemplateConfig).template,
});

/** Cookie an admin sets from the browser console to preview a layout (`0` or `1`). */
export const TEMPLATE_COOKIE_NAME = 'site_template';

/** `role_id` of the admin role in the access token — same value the invoice screens use. */
const ADMIN_ROLE_ID = 1;

/** Browser inputs for the admin cookie override — a token so specs stay deterministic. */
export interface TemplateOverrideSource {
  readCookie(): string | null;
  isAdminLoggedIn(): boolean;
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') {
    return null;
  }
  const prefix = `${name}=`;
  const entry = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));
  return entry === undefined ? null : entry.slice(prefix.length);
}

export const TEMPLATE_OVERRIDE_SOURCE = new InjectionToken<TemplateOverrideSource>(
  'TEMPLATE_OVERRIDE_SOURCE',
  {
    providedIn: 'root',
    factory: () => ({
      readCookie: () => readCookie(TEMPLATE_COOKIE_NAME),
      isAdminLoggedIn: () =>
        getRoleIdFromAccessToken() === ADMIN_ROLE_ID && !isStoredAccessTokenExpired(),
    }),
  },
);

/**
 * Resolves which site layout should render, for the whole app today and
 * per CMS section tomorrow.
 *
 * Single source of truth for template selection — no component should read
 * `environment.template` directly, so that when the backend starts sending a
 * per-row `template_id` only {@link resolveTemplateForRow} changes.
 *
 * Precedence: `site_template` cookie (honoured only while an admin is logged
 * in) → `environment.template` → classic. Admin preview from the console:
 * `document.cookie = "site_template=1; path=/"; location.reload();`
 */
@Injectable({ providedIn: 'root' })
export class TemplateService {
  private readonly configuredTemplate = inject(ENVIRONMENT_TEMPLATE);
  private readonly overrideSource = inject(TEMPLATE_OVERRIDE_SOURCE);

  /** Admin cookie override, read once so every section agrees for the page load. */
  private readonly override: SiteTemplate | null = this.readOverride();

  /**
   * Layout for the current tenant, available synchronously at construction
   * (no network dependency, safe to read during component initialisation).
   */
  readonly current: SiteTemplate = this.resolveTemplateForRow();

  /** True when the OTT layout is active — the common check in templates. */
  get isTemplate1(): boolean {
    return this.current === 1;
  }

  /**
   * Picks the layout for one CMS section row.
   *
   * Today `row` is deliberately ignored and every section renders the admin
   * override or the tenant's environment template. When the backend ships a per-row `template_id`
   * (mixed templates on one page), only this method body changes to read
   * `row?.template_id` first — callers already pass the row and need no edit.
   *
   * @param _row CMS section row; unused today, reserved for the per-row seam.
   * @returns The layout to render for that section.
   */
  resolveTemplateForRow(_row?: TemplateAwareRow): SiteTemplate {
    return this.override ?? this.toSiteTemplate(this.configuredTemplate);
  }

  /**
   * Reads the `site_template` cookie, ignoring it unless an admin is logged in
   * with an unexpired token — so visitors setting it from the console get nothing.
   *
   * @returns `0` or `1` from the cookie, otherwise `null`.
   */
  private readOverride(): SiteTemplate | null {
    const value = this.overrideSource.readCookie();
    if ((value !== '0' && value !== '1') || !this.overrideSource.isAdminLoggedIn()) {
      return null;
    }
    return this.toSiteTemplate(value);
  }

  /**
   * Narrows an untrusted config/API value to a known layout id.
   *
   * @param value Raw template id from environment or API.
   * @returns `1` only for an explicit 1, otherwise the classic design.
   */
  private toSiteTemplate(value: unknown): SiteTemplate {
    return Number(value) === 1 ? 1 : DEFAULT_TEMPLATE;
  }
}
